import { authBridge } from "@/lib/auth/bridge";

/**
 * Temporary plain-fetch helper used by early smoke-test pages.
 *
 * Replace per-page with the generated openapi-ts SDK as routes adopt typed
 * contracts. Keeps the surface small so this can be deleted entirely later.
 *
 * Throws an `ApiError` with the parsed ProblemDetails body so call sites can
 * branch on status without reading the response twice.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public problem: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = authBridge.getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // Only default Content-Type to JSON for the actual JSON case. FormData /
  // Blob / URLSearchParams / ReadableStream all need the browser to set the
  // boundary or media type itself — overriding it here would break multipart
  // uploads (drawing PNGs, document ingest) and any future binary POSTs.
  // Callers that hand-build a JSON string and don't want the default can
  // still set the header explicitly.
  if (
    init.body &&
    typeof init.body === "string" &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");

  const res = await fetch(path, { ...init, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      body,
      `${res.status} ${res.statusText} — ${init.method ?? "GET"} ${path}`,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
